import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { uniq } from 'lodash-es';
import { useQueries, useQuery } from '@tanstack/react-query';

import {
  AudienceGroup,
  AudienceGroupOption,
  SurveyAudienceBody,
  Survey,
} from '../../../../types/domainModels';
import { audienceGroupQueries } from 'hooks/backend/audienceGroup';
import { useHasRole } from 'hooks/users';
import {
  AUDIENCE_TARGET_TYPE,
  logicalModifierOptionsForTargetEnum,
  logicalModifierOptionsForTargetRange,
  pairifyNumberRange,
} from 'util/demographics';

import { HeaderWithEdit } from '../HeaderWithEdit/HeaderWithEdit';
import Tooltip from 'components/common/Tooltip';
import Icon from 'components/common/Icon';
import { getSurveyQuotasFromAudience } from 'components/surveyEdit/ReviewSurveySummary';
import { stripHtml } from 'util/richText';

export interface SurveyTarget {
  definitions: ReactNode[];
  questionTitle: string;
  audienceGroupId: number;
}

function getSurveyQuotasFromTargets(
  audienceTargets: SurveyAudienceBody[],
  allAudienceGroups: AudienceGroup[] | undefined,
  audienceGroupOptionsByGroupId: Map<number, AudienceGroupOption[]>,
): SurveyTarget[] {
  if (audienceTargets.length === 0) {
    return [];
  }
  const surveyTargets: SurveyTarget[] = [];

  audienceTargets.forEach((target) => {
    const foundTargetIndex = surveyTargets.findIndex(
      (existingTarget) =>
        existingTarget.audienceGroupId === target.audienceGroupId,
    );
    const audienceGroupOptionsResponse =
      audienceGroupOptionsByGroupId.get(target.audienceGroupId) ?? [];
    const surveyTarget: SurveyTarget = {
      definitions: [],
      questionTitle:
        allAudienceGroups?.find(
          (group: AudienceGroup) => group.id === target.audienceGroupId,
        )?.name || '',
      audienceGroupId: target.audienceGroupId,
    };
    const modifier = (
      target.audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE
        ? logicalModifierOptionsForTargetRange
        : logicalModifierOptionsForTargetEnum
    ).find((singleTarget) => singleTarget.value === target.logicalModifier);

    let options: string;
    if (target.audienceGroupId === AUDIENCE_TARGET_TYPE.RANGE) {
      options = pairifyNumberRange(target.numberRange)
        .map(({ start, end }) => `${start} - ${end}`)
        .join(' or ');
    } else if (target.audienceGroupId === AUDIENCE_TARGET_TYPE.OPEN_END) {
      options = target.numberSelections.join(' or ');
    } else {
      options = target.audienceGroupOptionSelections
        .map((groupOptionSelectionId) =>
          audienceGroupOptionsResponse.find(
            (singleOption: AudienceGroupOption) =>
              singleOption.id === groupOptionSelectionId,
          ),
        )
        .map((option) => option?.name)
        .join(' or ');
    }

    if (foundTargetIndex > -1) {
      surveyTargets[foundTargetIndex].definitions.push(
        <span>
          {Number(target.percentage) * 100}%{' '}
          <strong className="font-bold">{modifier?.label}</strong> {options}
        </span>,
      );
    } else {
      surveyTarget.definitions.push(
        <span>
          {Number(target.percentage) * 100}%{' '}
          <strong className="font-bold">{modifier?.label}</strong> {options}
        </span>,
      );
    }

    if (surveyTarget.definitions?.length) {
      surveyTargets.push(surveyTarget);
    }
  });
  return surveyTargets;
}

export const Targets = ({
  audienceTargets,
  survey,
}: {
  audienceTargets: SurveyAudienceBody[];
  survey: Survey;
}): JSX.Element => {
  const isAdmin = useHasRole('admin');
  const navigate = useNavigate();

  const { data: allAudienceGroups } = useQuery(audienceGroupQueries.list());

  const audienceGroupIds = uniq(
    audienceTargets.map((target) => target.audienceGroupId),
  );
  const audienceGroupOptionsQueries = useQueries({
    queries: audienceGroupIds.map((audienceGroupId) =>
      audienceGroupQueries.options({ audienceGroupId }),
    ),
  });
  const audienceGroupOptionsByGroupId = new Map<number, AudienceGroupOption[]>(
    audienceGroupIds.map((audienceGroupId, index) => [
      audienceGroupId,
      audienceGroupOptionsQueries[index]?.data ?? [],
    ]),
  );

  const surveyTargets = getSurveyQuotasFromTargets(
    audienceTargets,
    allAudienceGroups,
    audienceGroupOptionsByGroupId,
  );

  const surveyTargetsForComparison = surveyTargets.filter(
    (target) => target.questionTitle !== 'State',
  );
  const oldSurveyAudiences = getSurveyQuotasFromAudience(survey);

  return (
    <div className="space-y-2">
      <HeaderWithEdit
        onClickEdit={() => {
          navigate(`/surveys/${survey.id}/build/audience`);
        }}
        title="Audience Targets"
      />
      {
        // TODO: Once we're confident in the migration and new geo targetting feature, remove this
        isAdmin &&
          surveyTargetsForComparison.length !== oldSurveyAudiences.length && (
            <Tooltip
              trigger={
                <div className="inline-flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-black flex justify-center items-center">
                    <div className="w-4 h-4 text-red">
                      <Icon id="alert-triangle" />
                    </div>
                  </div>
                  Target and Audience mismatch, hover for more info
                </div>
              }
            >
              <div className="mb-2">
                We recently revamped our Audience support and some items may not
                have been successfully transferred over. Please take a look and
                compare the old to the new. If you notice a discrepancy, please
                let the tech team know.
              </div>
              <div className="mb-2">{`There are ${surveyTargetsForComparison.length} Target(s) and ${oldSurveyAudiences.length} Audience(s). The State Target is not part of this comparison since it doesn't exist in Audiences.`}</div>
              <div className="mb-2">{`Old Audience(s) are:`}</div>
              {oldSurveyAudiences.map(({ definitions, questionTitle }, i) => {
                return (
                  <div key={i}>
                    <p className="italic">{stripHtml(questionTitle)}</p>
                    <ul className="list-disc pl-8">
                      {definitions.map((definition, i) => {
                        return <li key={i}>{definition}</li>;
                      })}
                    </ul>
                  </div>
                );
              })}
            </Tooltip>
          )
      }
      {surveyTargets.length === 0 && <p>No targets</p>}
      {surveyTargets.map(({ definitions, questionTitle }, i) => {
        return (
          <div key={i}>
            <p className="italic">{stripHtml(questionTitle)}</p>
            <ul className="list-disc pl-8">
              {definitions.map((definition, i) => {
                return <li key={i}>{definition}</li>;
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};
